import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { DynamicEarnEvent } from '@flipster/utils/segment/events/dynamic-earn';

import { CurrencyDisplay } from '../../../misc/display';
import { useDepositModal } from '../../../shared/wallet-modal/deposit';
import { narsilMintOverviewBunja } from '../state';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const DepositToSubscribeModal = ({ isOpen, onClose, onExit }: Props) => {
  const openDepositModal = useDepositModal();
  const { availableBalanceAtom, minAmountAtom } = useBunja(narsilMintOverviewBunja);
  const availableBalance = useAtomValue(availableBalanceAtom);
  const minAmount = useAtomValue(minAmountAtom);

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title
          label={<FormattedMessage id='Earn.r251204.Deposit_to_subscribe_symbol' values={{ symbol: UsdtCurrency }} />}
        />
      </Modal.Header>
      <Modal.Content>
        <div
          className={stack({
            justify: 'center',
            gap: 12,
            p: 16,
            rounded: 8,
            bg: 'background.default.level-1',
          })}
        >
          <div className={hstack({ gap: 16 })}>
            <div
              className={stack({
                flex: 1,
                gap: 8,
              })}
            >
              <CurrencyDisplay
                currency={UsdtCurrency}
                icon={<CurrencyDisplay.Icon size={24} />}
                mainLabel={<CurrencyDisplay.Label value='shortName' />}
              />
              <p
                className={css({
                  textStyle: 'Text/L/Monospace/Strong',
                  color: 'content.default.level-5',
                })}
              >
                <Formatted.Number value={availableBalance} suffix={UsdtCurrency} precision={UsdtPrecision} toFixed />
              </p>
              <p
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-3',
                })}
              >
                <FormattedMessage
                  id='Mint.r251008.Min_amount_colon_amount_quote_currency'
                  values={{
                    amountQuoteCurrency: (
                      <Formatted.Number
                        key='minAmount'
                        value={minAmount}
                        suffix={UsdtCurrency}
                        precision={UsdtPrecision}
                        toFixed
                      />
                    ),
                  }}
                />
              </p>
            </div>
            <Button
              label={<FormattedMessage id='Autogen_common.r251126.s_Deposit' />}
              onClick={() => {
                segment.track(DynamicEarnEvent.DynamicEarnDepositDepositClick, {
                  product_name: 'Narsil USDT Prime',
                });
                onClose();
                openDepositModal();
              }}
            />
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
